import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaterialTable, { serialNumberColumn } from '../components/MaterialTableAdapter';
import { toast } from 'react-toastify';
import { Activity, BadgeCheck, CheckCheck, CircleCheck, CircleX, History, MessageSquareText, Search as SearchIcon, Send, Wallet, X } from 'lucide-react';
import { FilterCard } from '../ui';

import Button from '../components/Button';
import Loading from '../components/Loading';
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { getMessagingUsers, sendDirectMessage, getMessageTemplates, getUserMessageHistory } from '../actions/messagingActions';
import { SEND_DIRECT_MESSAGE_RESET, GET_USER_MESSAGE_HISTORY_RESET } from '../constants/messagingConstants';
import ScreenHeader from '../components/layout/ScreenHeader'
import { hasPermission } from '../constants/rolePermissions';

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const Messaging = () => {
    const dispatch = useDispatch();
    const tableRef = useRef(null);

    // Local State
    const [filters, setFilters] = useState({
        verification: 'all',
        minWalletBalance: 'all',
        activity: 'all',
        search: ''
    });
    const [searchInput, setSearchInput] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [messageContent, setMessageContent] = useState('');
    const [channel, setChannel] = useState('whatsapp');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);

    // History Drawer State
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [historyUser, setHistoryUser] = useState(null);

    // SMS character limit constants
    const SMS_CHAR_LIMIT = 160;
    const SMS_WARNING_THRESHOLD = 140;

    // Sending needs messagingSend (backend checks the same)
    const { userInfo: adminInfo } = useSelector(state => state.adminLogin || {});
    const canSendMessages = hasPermission(adminInfo?.role, 'messagingSend');

    // Redux State
    const { loadingGetMessagingUsers, getMessagingUsersData } = useSelector(state => state.getMessagingUsersReducer || {});

    const {
        loadingSendDirectMessage,
        successSendDirectMessage,
        sendDirectMessageData,
        errorSendDirectMessage
    } = useSelector(state => state.sendDirectMessageReducer || {});

    const { getMessageTemplatesData } = useSelector(state => state.getMessageTemplatesReducer || {});
    const { loadingGetUserMessageHistory, getUserMessageHistoryData } = useSelector(state => state.getUserMessageHistoryReducer || {});
    const users = getMessagingUsersData?.users || [];
    const templates = getMessageTemplatesData?.templates || [];

    // Initial Load
    useEffect(() => {
        dispatch(getMessagingUsers(filters));
        dispatch(getMessageTemplates());
    }, [dispatch]);

    useEffect(() => {
        if (getMessagingUsersData || !loadingGetMessagingUsers) {
            setLoading(false);
        }
    }, [getMessagingUsersData, loadingGetMessagingUsers]);

    // Handle send success/error
    useEffect(() => {
        if (successSendDirectMessage) {
            toast.success(sendDirectMessageData?.message || 'Messages sent successfully!');
            handleCloseDialog();
            dispatch({ type: SEND_DIRECT_MESSAGE_RESET });
        }
        if (errorSendDirectMessage) {
            toast.error(errorSendDirectMessage);
            dispatch({ type: SEND_DIRECT_MESSAGE_RESET });
        }
    }, [successSendDirectMessage, errorSendDirectMessage, sendDirectMessageData, dispatch]);

    // Fetch users when filters change (debounced for search)
    useEffect(() => {
        dispatch(getMessagingUsers(filters));
    }, [dispatch, filters]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchInput }));
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleOpenDialog = (users) => {
        if (!users || users.length === 0) {
            toast.warning('Please select at least one user');
            return;
        }
        setSelectedUsers(users);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setMessageContent('');
        setSelectedUsers([]);
        tableRef.current?.clearSelection();
        setSelectedTemplateId('');
        setShowConfirmation(false);
    };

    const handleShowConfirmation = () => {
        if (!messageContent.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setShowConfirmation(true);
    };

    const handleSendMessage = () => {
        const userIds = selectedUsers.map(u => u._id);
        dispatch(sendDirectMessage(userIds, messageContent, channel));
        setShowConfirmation(false);
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleTemplateChange = (templateId) => {
        setSelectedTemplateId(templateId);
        if (templateId) {
            const template = templates.find(t => t._id === templateId);
            if (template) {
                setMessageContent(template.content);
            }
        }
    };

    const handleOpenHistory = (user) => {
        setHistoryUser(user);
        setHistoryDrawerOpen(true);
        dispatch(getUserMessageHistory(user._id));
    };

    const handleCloseHistory = () => {
        setHistoryDrawerOpen(false);
        setHistoryUser(null);
        dispatch({ type: GET_USER_MESSAGE_HISTORY_RESET });
    };

    const handleSelectAllMatching = useCallback(() => {
        if (!tableRef.current || users.length === 0) return;

        const allSelected = selectedUsers.length === users.length;
        if (allSelected) {
            tableRef.current.clearSelection();
            toast.info('Selection cleared');
        } else {
            tableRef.current.selectAll();
            toast.success(`Selected ${users.length} users`);
        }
    }, [selectedUsers.length, users]);

    const getHeadCells = () => [
        serialNumberColumn,
        {
            field: 'name',
            title: 'Name',
            render: (rowData) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                        {rowData.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="font-medium text-gray-800">{rowData.name || 'Unknown'}</span>
                </div>
            ),
        },
        {
            field: 'phoneNumber',
            title: 'Phone',
            render: (rowData) => (
                <div className="flex items-center gap-1">
                    <WhatsAppIcon style={{ fontSize: 16, color: 'var(--cm-success)' }} />
                    <span className="text-sm text-gray-600">{rowData.phoneNumber || '-'}</span>
                </div>
            ),
        },
        {
            field: 'email',
            title: 'Email',
            render: (rowData) => (
                <span className="text-sm text-gray-600">{rowData.email || '-'}</span>
            ),
        },
        {
            field: 'verification',
            title: 'Verified',
            render: (rowData) => (
                rowData.verification?.isFullyVerified ? (
                    <span className="px-3 py-1 rounded text-xs font-bold bg-green-100 text-green-800 inline-flex items-center gap-1">
                        <CircleCheck size={15} /> Verified
                    </span>
                ) : (
                    <span className="px-3 py-1 rounded text-xs font-bold bg-red-100 text-red-800 inline-flex items-center gap-1">
                        <CircleX size={15} /> Unverified
                    </span>
                )
            ),
        },
        {
            field: 'lastActive',
            title: 'Last Active',
            render: (rowData) => (
                <span className="text-sm text-gray-600">
                    {rowData.lastActive ? new Date(rowData.lastActive).toLocaleDateString() : 'Never'}
                </span>
            ),
        },
        {
            title: 'Actions',
            sorting: false,
            render: (rowData) => (
                <button
                    onClick={() => handleOpenHistory(rowData)}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded-lg transition-colors"
                    title="View Message History"
                >
                    <History size={18} />
                </button>
            ),
        },
    ];

    if (loading) {
        return <Loading />;
    }

    const filterChips = [
        searchInput.trim() && { icon: SearchIcon, label: `"${searchInput.trim()}"` },
        filters.verification !== 'all' && { icon: BadgeCheck, label: filters.verification === 'verified' ? 'Verified only' : 'Unverified only' },
        filters.minWalletBalance !== 'all' && { icon: Wallet, label: `Balance > ₹${Number(filters.minWalletBalance).toLocaleString('en-IN')}` },
        filters.activity !== 'all' && { icon: Activity, label: filters.activity === 'active' ? 'Active (30 days)' : 'Inactive (>30 days)' },
    ].filter(Boolean);

    return (
        <div className="w-full h-full">
            <ScreenHeader title="Messaging" />

            {/* Filter Section */}
            <FilterCard
                id="messaging-filters"
                className="my-4"
                inlineToggle
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                chips={filterChips}
                emptyLabel="All users"
                aside={
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">Found: {users.length} users</span>
                            <button
                                type="button"
                                onClick={handleSelectAllMatching}
                                disabled={users.length === 0}
                                className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold ring-1 ring-inset transition-all focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:cursor-not-allowed disabled:opacity-50 ${selectedUsers.length === users.length && users.length > 0 ? 'bg-violet-600 text-white ring-violet-600 hover:bg-violet-700' : 'bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100'}`}
                            >
                                {selectedUsers.length === users.length && users.length > 0 ? <X size={18} /> : <CheckCheck size={18} />}
                                {selectedUsers.length === users.length && users.length > 0 ? 'Clear All' : 'Select All'}
                                {selectedUsers.length > 0 && (
                                    <span className="rounded-full bg-white/70 px-1.5 text-xs text-violet-700">{selectedUsers.length}</span>
                                )}
                            </button>
                        </div>
                }
            >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">Search</label>
                        <div className="mt-1 relative">
                            <input
                                type="text"
                                placeholder="Name, phone, or email"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="block w-full border border-gray-300 rounded-md py-2 pr-3 text-sm"
                                style={{ paddingLeft: '2.25rem' }}
                            />
                            <Search
                                className="absolute left-2 text-gray-400 pointer-events-none"
                                style={{ fontSize: 20, top: '50%', transform: 'translateY(-50%)' }}
                            />
                        </div>
                    </div>

                    {/* Verification Status */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">Verification Status</label>
                        <select
                            value={filters.verification}
                            onChange={(e) => handleFilterChange('verification', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="all">All Users</option>
                            <option value="verified">Verified Only</option>
                            <option value="unverified">Unverified Only</option>
                        </select>
                    </div>

                    {/* Wallet Balance */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">Wallet Balance</label>
                        <select
                            value={filters.minWalletBalance}
                            onChange={(e) => handleFilterChange('minWalletBalance', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="all">Any Amount</option>
                            <option value="10000">More than ₹10k</option>
                            <option value="20000">More than ₹20k</option>
                            <option value="50000">More than ₹50k</option>
                            <option value="100000">More than ₹1L</option>
                        </select>
                    </div>

                    {/* Activity */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">Activity</label>
                        <select
                            value={filters.activity}
                            onChange={(e) => handleFilterChange('activity', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="all">All Activity</option>
                            <option value="active">Active (Last 30 Days)</option>
                            <option value="inactive">Inactive ({'>'}30 Days)</option>
                        </select>
                    </div>

                </div>
            </FilterCard>

            {/* User List Table */}
            <MaterialTable
                tableRef={tableRef}
                icons={tableIcons}
                title="User List"
                columns={getHeadCells()}
                data={users}
                isLoading={loadingGetMessagingUsers}
                options={{
                    selection: true,
                    search: false,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 50, 100],
                    rowStyle: {
                        height: '5px',
                        fontSize: 13,
                    },
                    paging: true,
                    emptyRowsWhenPaging: false,
                    headerStyle: {
                        position: 'sticky',
                        top: '0',
                        fontWeight: 'bold',
                    },
                }}
                actions={canSendMessages ? [
                    {
                        tooltip: 'Send Message to Selected',
                        icon: () => (
                            <Button
                                type="button"
                                text="Send Message"
                                className="bg-emerald-500 hover:bg-emerald-700 flex items-center gap-1"
                            />
                        ),
                        onClick: (evt, data) => handleOpenDialog(data)
                    }
                ] : []}
                onSelectionChange={(rows) => setSelectedUsers(rows)}
            />

            {/* Send Message Modal */}
            {openDialog && (
                <>
                    <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
                    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
                        <div className="relative w-full max-w-lg mx-auto bg-white rounded-lg shadow-lg my-8">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-emerald-500 rounded-t-lg">
                                <div className="flex items-center gap-2 text-white">
                                    <MessageSquareText size={20} />
                                    <h3 className="text-lg font-semibold">Send Message to {selectedUsers.length} User(s)</h3>
                                </div>
                                <button onClick={handleCloseDialog} className="text-white hover:text-gray-200">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
                                {/* Channel Selection */}
                                <p className="text-sm font-medium text-gray-700 mb-2">Select Channel:</p>
                                <div className="flex gap-4 mb-4">
                                    <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${channel === 'whatsapp' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}>
                                        <input
                                            type="radio"
                                            name="channel"
                                            value="whatsapp"
                                            checked={channel === 'whatsapp'}
                                            onChange={() => setChannel('whatsapp')}
                                            className="hidden"
                                        />
                                        <WhatsAppIcon style={{ color: 'var(--cm-success)' }} />
                                        <span className={`font-medium ${channel === 'whatsapp' ? 'text-green-700' : 'text-gray-600'}`}>WhatsApp</span>
                                    </label>
                                    <label className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${channel === 'sms' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                                        <input
                                            type="radio"
                                            name="channel"
                                            value="sms"
                                            checked={channel === 'sms'}
                                            onChange={() => setChannel('sms')}
                                            className="hidden"
                                        />
                                        <MessageSquareText size={20} className="text-blue-500" />
                                        <span className={`font-medium ${channel === 'sms' ? 'text-blue-700' : 'text-gray-600'}`}>SMS</span>
                                    </label>
                                </div>

                                {/* SMS Warning */}
                                {channel === 'sms' && messageContent.length > SMS_WARNING_THRESHOLD && (
                                    <div className={`mb-4 p-3 rounded-lg ${messageContent.length > SMS_CHAR_LIMIT ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        ⚠️ SMS messages over {SMS_CHAR_LIMIT} characters may be split into multiple messages.
                                    </div>
                                )}

                                {/* Template Picker */}
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-gray-700">Select Template (Optional)</label>
                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => handleTemplateChange(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">Custom Message</option>
                                        {templates.map(template => (
                                            <option key={template._id} value={template._id}>
                                                {template.name} {template.includesBonus ? `(+₹${template.defaultBonusAmount})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Message Content */}
                                <div className="mb-4">
                                    <label className="text-sm font-medium text-gray-700">Message Content</label>
                                    <textarea
                                        value={messageContent}
                                        onChange={(e) => setMessageContent(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                        rows="4"
                                        placeholder="Enter your message..."
                                    />
                                    <div className="flex justify-between mt-1">
                                        <span className="text-xs text-gray-500">Use {'{name}'} as a placeholder for user's name</span>
                                        {channel === 'sms' && (
                                            <span className={`text-xs font-medium ${messageContent.length > SMS_CHAR_LIMIT ? 'text-red-600' : messageContent.length > SMS_WARNING_THRESHOLD ? 'text-yellow-600' : 'text-gray-500'}`}>
                                                {messageContent.length}/{SMS_CHAR_LIMIT}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Preview */}
                                {messageContent.trim() && (
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-500 font-medium mb-2">PREVIEW (First recipient):</p>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                            {messageContent.replace(/{name}/g, selectedUsers[0]?.name || 'User')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end px-4 py-3 border-t border-gray-200 gap-2">
                                <button
                                    onClick={handleCloseDialog}
                                    className="text-gray-500 font-medium px-4 py-2 hover:text-gray-700"
                                >
                                    Cancel
                                </button>
                                <Button
                                    type="button"
                                    onClick={handleShowConfirmation}
                                    text="Preview & Send"
                                    disabled={loadingSendDirectMessage || !messageContent.trim()}
                                    className="bg-emerald-500 hover:bg-emerald-700"
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Confirmation Modal */}
            {showConfirmation && (
                <>
                    <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
                    <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-auto my-6 mx-auto max-w-md">
                            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                <div className="flex items-center justify-between py-3 px-4 border-b border-gray-200">
                                    <div className="flex items-center gap-2">
                                        {channel === 'whatsapp' ? (
                                            <WhatsAppIcon style={{ color: 'var(--cm-success)' }} />
                                        ) : (
                                            <MessageSquareText size={20} className="text-blue-500" />
                                        )}
                                        <h3 className="text-lg font-semibold">Confirm Send</h3>
                                    </div>
                                </div>
                                <div className="relative p-6 text-center">
                                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${channel === 'whatsapp' ? 'bg-green-100' : 'bg-blue-100'}`}>
                                        {channel === 'whatsapp' ? (
                                            <WhatsAppIcon style={{ fontSize: 32, color: 'var(--cm-success)' }} />
                                        ) : (
                                            <MessageSquareText size={32} className="text-blue-500" />
                                        )}
                                    </div>
                                    <p className="text-gray-600 mb-2">
                                        You are about to send a <strong>{channel.toUpperCase()}</strong> message to:
                                    </p>
                                    <p className="text-3xl font-bold text-emerald-600 mb-2">
                                        {selectedUsers.length} user(s)
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        This action cannot be undone.
                                    </p>
                                </div>
                                <div className="flex items-center justify-center py-3 px-4 border-t border-gray-200 gap-3">
                                    <button
                                        className="text-gray-500 font-medium px-6 py-2 hover:text-gray-700"
                                        onClick={handleCancelConfirmation}
                                    >
                                        Go Back
                                    </button>
                                    <button
                                        className={`font-bold uppercase text-sm px-6 py-2 rounded shadow text-white flex items-center gap-2 ${channel === 'whatsapp' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} disabled:bg-gray-300`}
                                        onClick={handleSendMessage}
                                        disabled={loadingSendDirectMessage}
                                    >
                                        {loadingSendDirectMessage ? 'Sending...' : 'Confirm Send'}
                                        <Send size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Message History Drawer */}
            {historyDrawerOpen && (
                <>
                    <div className="fixed inset-0 z-40 bg-black opacity-25" onClick={handleCloseHistory}></div>
                    <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col">
                        {/* Header */}
                        <div className="bg-emerald-500 text-white p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-semibold">Message History</h3>
                                    {historyUser && (
                                        <div className="mt-2">
                                            <p className="font-medium">{historyUser.name}</p>
                                            <p className="text-sm opacity-80">{historyUser.phoneNumber}</p>
                                        </div>
                                    )}
                                </div>
                                <button onClick={handleCloseHistory} className="text-white hover:text-gray-200">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                            {loadingGetUserMessageHistory ? (
                                <div className="flex justify-center items-center h-full">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                                </div>
                            ) : getUserMessageHistoryData?.logs?.length === 0 ? (
                                <div className="text-center py-12">
                                    <History size={48} className="mx-auto text-gray-300" />
                                    <p className="text-gray-500 mt-2 font-medium">No messages sent yet</p>
                                    <p className="text-sm text-gray-400">This user hasn't received any messages</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {getUserMessageHistoryData?.logs?.map((log, index) => (
                                        <div key={log._id || index} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex justify-between items-center mb-2">
                                                <div className="flex items-center gap-2">
                                                    {log.channel === 'whatsapp' ? (
                                                        <WhatsAppIcon style={{ fontSize: 18, color: 'var(--cm-success)' }} />
                                                    ) : (
                                                        <MessageSquareText size={18} className="text-blue-500" />
                                                    )}
                                                    <span className="text-sm font-medium text-gray-700">{log.channel?.toUpperCase()}</span>
                                                </div>
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${log.status === 'sent' || log.status === 'delivered' ? 'bg-green-100 text-green-800' : log.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {log.status}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-2">
                                                {log.message?.length > 100 ? `${log.message.substring(0, 100)}...` : log.message}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(log.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Messaging;
